"use client";
import {
  Box,
  chakra,
  Heading,
  Text,
  Card,
  CardBody,
  LinkBox,
  LinkOverlay,
} from "@chakra-ui/react";
import { CardProps } from "../card";
import { Chip } from "@/components/atoms/chip";
import { ChipVariants, FundsTagType } from "@/types/consts";
import ComponentProps from "@/baseComponents/types/ComponentProps";
import { MediaImage } from "@/components/atoms/media";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import { useTranslations } from "next-intl";
import { PageTypes } from "@/types/enums";

export interface FundsCardProps extends CardProps {
  contentType?: PageTypes;
  tags?: {
    type: (typeof FundsTagType)[keyof typeof FundsTagType];
    value: string;
  }[];
  isOpenForInvestment?: boolean;
}

export interface FundsOverviewCardProps extends ComponentProps {
  index: number;
  card: FundsCardProps;
}

export default chakra(FundsOverviewCard);
function FundsOverviewCard({
  index,
  card,
  theme = "brand",
}: FundsOverviewCardProps) {
  const t = useTranslations();

  return (
    <LinkBox
      as={Card}
      display="grid"
      gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
      gap={2}
      h="full"
      boxShadow="none"
      cursor="pointer"
    >
      {card.mediaData && (
        <Box position="relative" overflow="hidden" rounded="1rem">
          <MediaImage
            {...card.mediaData}
            viewportWidthFactor={{ base: 1 }}
            cropSizeAlias={{ base: "default" }}
            imageUrlBuilder={buildImageUrl}
            aspectRatio={{ base: "16 / 9", md: index < 3 ? "16 / 9" : "4 / 5" }}
          />
          {card.isOpenForInvestment === false && (
            <Box
              position="absolute"
              inset={0}
              backgroundColor={`${theme}.white`}
              opacity={0.4}
            />
          )}
        </Box>
      )}
      <CardBody
        display="flex"
        flexDirection="column"
        gap={4}
        p={{ base: 0, md: 5 }}
        pb={{ base: 8 }}
        borderBottom="1px solid"
        borderTop={{ md: "1px solid" }}
        borderColor={{ base: "gray.200", md: "gray.200" }}
      >
        {card.contentType === PageTypes.FundsDetailPage && (
          <Chip
            variant={ChipVariants.Text}
            ellipse
            ellipseColor={
              !card.isOpenForInvestment ? `${theme}.aubergine` : undefined
            }
            text={
              card.isOpenForInvestment
                ? t("investment Open")
                : t("investment Closed")
            }
          />
        )}
        <Heading
          textStyle={index < 3 ? "display.md" : { lg: "display.xs" }}
          fontWeight={400}
          color={
            card.isOpenForInvestment === false
              ? "gray.600"
              : `${theme}.aubergine`
          }
        >
          <LinkOverlay
            href={card.link?.url}
            _hover={{ textDecoration: "none" }}
          >
            {card.title}
          </LinkOverlay>
        </Heading>
        <Text
          display={{ base: "block", md: index < 3 ? "block" : "none" }}
          maxW={"33.5rem"}
          textStyle="paragrpah.md"
          dangerouslySetInnerHTML={{
            __html: card.text ?? "",
          }}
        />
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          maxW="33.5rem"
          mt={{ base: 4, md: 6 }}
        >
          {card.tags?.map((tag, index) => (
            //--index as key is accepted, list is static--//
            <Chip
              key={index}
              variant={ChipVariants.Outline}
              text={
                tag.type === FundsTagType.MinimumInvestment
                  ? `${t("minimum Investment Label")}: ${tag.value}`
                  : tag.value
              }
              isDimmed={!card.isOpenForInvestment}
            />
          ))}
        </Box>
      </CardBody>
    </LinkBox>
  );
}
